import { QueryFilters, useQueryClient } from "@tanstack/react-query";
import { trpc } from "../trpc";
import { getQueryKey } from "@trpc/react-query";

import { StorageZap } from "./useQueryStorageZaps";
import { OrganizationZaps } from "./useQueryOrganizationZaps";
import { SearchZaps } from "./useQuerySearchZaps";
import { Asset } from "@zapier/bff";

interface Input {
  folderId?: string; // used for cache invalidation
}

export function useDeleteZaps(input?: Input) {
  const { folderId } = input || {};
  const utils = trpc.useUtils();

  const queryClient = useQueryClient();
  const mutation = trpc.zap.deleteZaps.useMutation({
    // Callback to handle optimistic updates in RQ cache
    onMutate: ({ zapIds, projectId }) => {
      // Update Zap under searchZaps key (search results view)
      const searchKey = getQueryKey(trpc.zap.searchZaps, undefined, "any");
      queryClient.cancelQueries(searchKey as QueryFilters); // cancel outgoing refetches
      queryClient.setQueriesData(
        {
          queryKey: searchKey,
          type: "active",
        },
        (old: SearchZaps | undefined) => {
          // Convert zapIds array to a Set for faster lookups
          const zapIdsSet = new Set(zapIds);
          // Remove the deleted Zaps from the results array
          if (old?.results?.some((zap) => zapIdsSet.has(String(zap.id)))) {
            return {
              ...old,
              results: old.results.filter(
                (zap) => !zapIdsSet.has(String(zap.id)),
              ),
            };
          }

          return old;
        },
      );

      // Update Zap under storageZaps key (search results view)
      const storageKey = getQueryKey(trpc.zap.storageZaps, undefined, "any");
      queryClient.cancelQueries(storageKey as QueryFilters); // cancel outgoing refetches
      queryClient.setQueriesData(
        {
          queryKey: storageKey,
          type: "active",
        },
        (old: Record<string, StorageZap> | undefined) => {
          if (old) {
            const hasMatchingIds = zapIds.some((id) => id in old);

            if (hasMatchingIds) {
              // Remove the deleted Zaps from the cache hash
              const newHash = Object.keys(old)
                .filter((key) => !zapIds.includes(key))
                .reduce((accumulator: Record<string, StorageZap>, key) => {
                  accumulator[key] = old[key];
                  return accumulator;
                }, {});

              return newHash;
            }
          }

          return old;
        },
      );

      // Update Zap under organizationZaps key (single folder view)
      const orgKey = getQueryKey(trpc.zap.organizationZaps, undefined, "any");
      queryClient.cancelQueries(orgKey as QueryFilters); // cancel outgoing refetches
      queryClient.setQueriesData(
        {
          queryKey: orgKey,
          type: "active",
        },
        (old: OrganizationZaps | undefined) => {
          // Convert zapIds array to a Set for faster lookups
          const zapIdsSet = new Set(zapIds);
          // Remove the deleted Zaps from the results array
          if (old?.results?.some((zap) => zapIdsSet.has(String(zap.id)))) {
            return {
              ...old,
              results: old.results.filter(
                (zap) => !zapIdsSet.has(String(zap.id)),
              ),
            };
          }

          return old;
        },
      );

      // Remove Zap from project
      if (projectId) {
        const assetListKey = getQueryKey(trpc.asset.list, { projectId });
        queryClient.cancelQueries({ queryKey: assetListKey }); // cancel outgoing refetches
        queryClient.setQueriesData(
          {
            queryKey: assetListKey,
            type: "all",
          },
          (old: Asset[] | undefined) => {
            // Convert zapIds array to a Set for faster lookups
            const zapIdsSet = new Set(zapIds);
            // Remove the deleted Zaps from the results array
            return (old || []).filter(
              (asset) => !zapIdsSet.has(String(asset.nodeId)),
            );
          },
        );
      }
    },
    onSettled: (_data, _errors, { projectId }) => {
      const searchKey = getQueryKey(trpc.zap.searchZaps, undefined, "any");
      queryClient.refetchQueries({ queryKey: searchKey });
      const storageKey = getQueryKey(trpc.zap.storageZaps, undefined, "any");
      queryClient.refetchQueries({ queryKey: storageKey });
      const orgKey = getQueryKey(trpc.zap.organizationZaps, undefined, "any");
      queryClient.refetchQueries({ queryKey: orgKey });
      if (projectId) {
        const assetListKey = getQueryKey(trpc.asset.list, { projectId });
        queryClient.refetchQueries({ queryKey: assetListKey });
      }
      if (folderId) {
        utils.asset.getHydratedFolderAssets.invalidate({
          folderId,
        });
      }
    },
  });

  return {
    deleteZaps: mutation.mutate,
    deleteZapsAsync: mutation.mutateAsync,
    isLoading: mutation.isPending,
  };
}
